// src/components/common/AttendanceRegularizationModal.js
import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock } from 'lucide-react';
import attendanceService from '../../services/attendance.service';

const formatLocalDate = (daysOffset = 0) => {
    const now = new Date();
    now.setDate(now.getDate() + daysOffset);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Check if shift is overnight (end time before start time)
const isOvernightShift = (shiftStartTime, shiftEndTime) => {
    if (!shiftStartTime || !shiftEndTime) return false;
    const [startHour, startMin] = shiftStartTime.split(':').map(Number);
    const [endHour, endMin] = shiftEndTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes < startMinutes;
};

const AttendanceRegularizationModal = ({
    isOpen,
    onClose,
    managerId,
    onSubmitSuccess = () => {},
    initialDate,
    initialType = 'missed_punch',
    shiftStartTime,
    shiftEndTime
}) => {
    // For night shift employees, allow selecting tomorrow's date (+1 day)
    const isNightShift = isOvernightShift(shiftStartTime, shiftEndTime);
    const maxDateOffset = isNightShift ? 1 : 0;
    const [formData, setFormData] = useState({
        attendanceDate: initialDate || formatLocalDate(),
        regularizationType: initialType,
        punchMode: 'both', // 'check_in', 'check_out', or 'both'
        requestedPunchIn: '',
        requestedPunchOut: '',
        reason: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (isOpen) {
            setFormData({
                attendanceDate: initialDate || formatLocalDate(),
                regularizationType: initialType,
                punchMode: 'both',
                requestedPunchIn: '',
                requestedPunchOut: '',
                reason: ''
            });
            setError('');
            setSuccess('');
        }
    }, [isOpen, initialDate, initialType]);

    if (!isOpen) {
        return null;
    }

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        // Allow submission even without manager - admin will receive requests without manager_id
        // if (!managerId) {
        //     setError('Unable to submit request because reporting manager information is missing.');
        //     return;
        // }

        if (!formData.reason.trim()) {
            setError('Please provide a reason for this request.');
            return;
        }

        // Validate based on punch mode
        const needsCheckIn = formData.punchMode === 'check_in' || formData.punchMode === 'both';
        const needsCheckOut = formData.punchMode === 'check_out' || formData.punchMode === 'both';

        if ((formData.regularizationType === 'missed_punch' ||
            formData.regularizationType === 'forgot_checkin' ||
            formData.regularizationType === 'forgot_checkout' ||
            formData.regularizationType === 'correction')
        ) {
            if (needsCheckIn && !formData.requestedPunchIn) {
                setError('Please specify the punch in time.');
                return;
            }
            if (needsCheckOut && !formData.requestedPunchOut) {
                setError('Please specify the punch out time.');
                return;
            }
        }

        // For overtime_comp_off, punch times are recommended but not required (they already worked)
        if (formData.regularizationType === 'overtime_comp_off') {
            if (needsCheckIn && !formData.requestedPunchIn) {
                setError('Please specify the punch in time for overtime calculation.');
                return;
            }
            if (needsCheckOut && !formData.requestedPunchOut) {
                setError('Please specify the punch out time for overtime calculation.');
                return;
            }
        }

        try {
            setSubmitting(true);
            const payload = {
                ...formData,
                managerId
            };
            const result = await attendanceService.submitRegularizationRequest(payload);

            if (result.success) {
                setSuccess(result.message || 'Regularization request submitted successfully.');
                onSubmitSuccess(result.data);
            } else {
                setError(result.error || 'Failed to submit regularization request.');
            }

            setTimeout(() => {
                setSuccess('');
                setError('');
                onClose();
            }, 1200);
        } catch (submitError) {
            console.error('Regularization submit error:', submitError);
            const message =
                submitError.response?.data?.error ||
                submitError.response?.data?.message ||
                submitError.message ||
                'Failed to submit regularization request.';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
            <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Attendance Regularization
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            {success}
                        </div>
                    )}

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Attendance Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={formData.attendanceDate}
                                max={formatLocalDate(maxDateOffset)}
                                onChange={(event) => handleChange('attendanceDate', event.target.value)}
                                required
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Regularization Type
                            </label>
                            <select
                                value={formData.regularizationType}
                                onChange={(event) => handleChange('regularizationType', event.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="missed_punch">Missed Punch</option>
                                <option value="forgot_checkin">Forgot Check-In</option>
                                <option value="forgot_checkout">Forgot Check-Out</option>
                                <option value="correction">Correction</option>
                                <option value="overtime_comp_off">Overtime & Comp-Off Request</option>
                            </select>
                        </div>

                        {/* Punch Mode Selector - allows separate check-in/check-out */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                What do you need to regularize?
                            </label>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleChange('punchMode', 'both')}
                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                                        formData.punchMode === 'both'
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    Both
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange('punchMode', 'check_in')}
                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                                        formData.punchMode === 'check_in'
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    Check-in Only
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChange('punchMode', 'check_out')}
                                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                                        formData.punchMode === 'check_out'
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    Check-out Only
                                </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {formData.punchMode === 'check_in' && "I checked out normally but missed my check-in"}
                                {formData.punchMode === 'check_out' && "I checked in normally but missed my check-out"}
                                {formData.punchMode === 'both' && "I need to regularize both check-in and check-out"}
                            </p>
                        </div>

                        <div className={`grid grid-cols-1 gap-4 ${(formData.punchMode === 'check_in' || formData.punchMode === 'check_out') ? '' : 'sm:grid-cols-2'}`}>
                            {/* Show Check-in input if punchMode is 'both' or 'check_in' */}
                            {(formData.punchMode === 'both' || formData.punchMode === 'check_in') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Requested Check-In Time <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="time"
                                            value={formData.requestedPunchIn}
                                            onChange={(event) => handleChange('requestedPunchIn', event.target.value)}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-9 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                            {/* Show Check-out input if punchMode is 'both' or 'check_out' */}
                            {(formData.punchMode === 'both' || formData.punchMode === 'check_out') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Requested Check-Out Time <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="time"
                                            value={formData.requestedPunchOut}
                                            onChange={(event) => handleChange('requestedPunchOut', event.target.value)}
                                            className="w-full rounded-lg border border-gray-300 px-3 py-2 pl-9 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                            required
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Reason <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={formData.reason}
                                onChange={(event) => handleChange('reason', event.target.value)}
                                rows={4}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                placeholder="Explain why the regularization is needed"
                                required
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:ring-gray-500"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-blue-400"
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting…' : 'Submit Request'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AttendanceRegularizationModal;
